import React from 'react';
import { List } from 'immutable';
import { Popover } from '@milanote/roma';
import { GridList } from 'react-aria-components';

// Components
import CreateLabelListItem from './CreateLabelListItem';
import EmptyResultsListItem from './EmptyResultsListItem';
import LabelListSection from './LabelListSection';
import useLabelListData from '../hooks/useLabelListData';
import useLabelListSelection from '../hooks/useLabelListSelection';

// Utils
import { useTranslator } from '../../../../../../components/i18n/useTranslator';

// Types
import { Label } from '../../../../../../../common/labels/labelTypes';
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';

type Props = {
    handleToggleLabelSelection: (labelId: string) => void;
    handleCreateLabel: () => void;
    elements: List<ImMNElement>;
    searchString: string;
    allLabels: Label[];
};

const LabelLists = (props: Props) => {
    const { elements, searchString, allLabels, handleToggleLabelSelection, handleCreateLabel } = props;
    const { allLabelsFiltered, currentLabelsFiltered, hasSearchResults, isSearching } = useLabelListData({
        allLabels,
        searchString,
    });
    const { checkedLabelIds, editingLabelId, gridListInteractionProps, handleEditingChange, handleSelectionChange } =
        useLabelListSelection({
            elements,
            handleCreateLabel,
            handleToggleLabelSelection,
        });
    const translate = useTranslator('popup', 'labels.menu');

    return (
        <div className="LabelLists">
            <Popover.Body>
                <GridList
                    {...gridListInteractionProps}
                    aria-label="Labels"
                    selectedKeys={checkedLabelIds}
                    onSelectionChange={handleSelectionChange}
                    className="w-full outline-hidden"
                >
                    <CreateLabelListItem searchString={searchString} />

                    {hasSearchResults && isSearching && (
                        <LabelListSection
                            checkedLabelIds={checkedLabelIds}
                            editingLabelId={editingLabelId}
                            labels={allLabelsFiltered}
                            onEditingChange={handleEditingChange}
                            sectionId="search-results-section"
                            showSeparator={true}
                        />
                    )}

                    {hasSearchResults &&
                        !isSearching &&
                        currentLabelsFiltered.map(({ id, title, labels }, index) => (
                            <LabelListSection
                                key={id}
                                checkedLabelIds={checkedLabelIds}
                                editingLabelId={editingLabelId}
                                header={title}
                                labels={labels}
                                onEditingChange={handleEditingChange}
                                sectionClassName={index === 0 ? 'pb-2' : undefined}
                                sectionId={`${id}-section`}
                                showSeparator={index > 0}
                            />
                        ))}

                    {!hasSearchResults && isSearching && <EmptyResultsListItem text={translate('noResults')} />}
                </GridList>
            </Popover.Body>
        </div>
    );
};

export default LabelLists;
